import React from 'react';
import { Box, CircularProgress, InputAdornment, TextField, Typography, useTheme } from '@mui/material';
import { ArrowRight, Check } from 'lucide-react';
import LinkedInMuiIcon from '@mui/icons-material/LinkedIn';
import { Primary } from 'ui-components/Buttons';
import GradientIcon from 'ui-components/icons/GradientIcon';

// Field icons use the brand gradient (#7852F8 -> #84AEF6) via the shared
// GradientIcon, not a flat colour. Pass a lucide component; opt out with
// gradient={false} for marks that carry their own colour (the LinkedIn "in").
// Field icons render FLAT indigo (#6366F1) to match startup-web's onboarding-v2,
// whose inputs use `<Icon color={ICON_COLOR} strokeWidth={1.7} />` — not the brand
// gradient. The gradient variant stays available behind the opt-in `gradient` prop.
export const FieldIcon = ({ icon: Icon, size = 18, gradient = false }) =>
  gradient ? (
    <GradientIcon icon={Icon} size={size} strokeWidth={1.7} />
  ) : (
    <Icon size={size} color={ICON_COLOR} strokeWidth={1.7} />
  );

// ---------------------------------------------------------------------------
// Design tokens — taken from startup-web's views/profile/onboarding-v2.
// Only the presentation is shared with startup-web; every field/hook below the
// primitives is investor-web's own.
// ---------------------------------------------------------------------------
export const INDIGO = '#4F46E5';
export const ICON_COLOR = '#6366F1';
export const BORDER = '#E4E7EC';
export const BORDER_HOVER = '#C7CAD1';
export const PLACEHOLDER = '#98A2B3';
export const TEXT_COLOR = '#101828';
export const MUTED = '#667085';
export const PAGE_BG = '#fffdfa';

export const fieldSx = {
  '& .MuiFilledInput-root': {
    backgroundColor: '#fff',
    border: `1px solid ${BORDER}`,
    borderRadius: '12px',
    overflow: 'hidden',
    transition: 'border-color .15s ease, box-shadow .15s ease',
    '&:hover': { backgroundColor: '#fff', borderColor: BORDER_HOVER },
    '&.Mui-error': { borderColor: '#F04438' },
  },
  '& .MuiFilledInput-input': {
    padding: '15px 14px',
    fontSize: '0.95rem',
    color: TEXT_COLOR,
    '&:-webkit-autofill': {
      WebkitBoxShadow: '0 0 0 1000px #fff inset',
      WebkitTextFillColor: TEXT_COLOR,
    },
  },
  '& .MuiFilledInput-input::placeholder': { color: PLACEHOLDER, opacity: 1 },
  '& .MuiInputAdornment-root': { marginTop: '0 !important', marginLeft: '12px' },
};

// Keeps the Autocomplete/Select slots the same height as the plain inputs.
export const autocompleteSlotSx = {
  '& .MuiFilledInput-root': {
    backgroundColor: '#fff',
    border: `1px solid ${BORDER}`,
    borderRadius: '12px',
    paddingTop: '0 !important',
    paddingBottom: '0 !important',
    paddingLeft: '0 !important',
    '&:hover': { backgroundColor: '#fff', borderColor: BORDER_HOVER },
    '&:before, &:after': { display: 'none' },
    '&.Mui-error': { borderColor: '#F04438' },
    // State stays disabled until a country is picked, City until a State is —
    // the sx above forces a white bg, so spell the disabled look out.
    '&.Mui-disabled': {
      backgroundColor: '#F9FAFB',
      borderColor: BORDER,
      cursor: 'not-allowed',
      '&:hover': { backgroundColor: '#F9FAFB', borderColor: BORDER },
    },
  },
  '& .MuiFilledInput-input': {
    padding: '15px 14px !important',
    fontSize: '0.95rem',
    color: TEXT_COLOR,
  },
  '& .MuiFilledInput-input.Mui-disabled': {
    WebkitTextFillColor: PLACEHOLDER,
    cursor: 'not-allowed',
  },
  '& .MuiFilledInput-input::placeholder': { color: PLACEHOLDER, opacity: 1 },
  '& .MuiInputAdornment-root': { marginTop: '0 !important', marginLeft: '12px' },
};

export const selectSx = {
  width: '100%',
  borderRadius: '12px',
  background: '#fff',
  border: `1px solid ${BORDER}`,
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '15px 14px !important',
  },
  '&:hover': { borderColor: BORDER_HOVER },
  '&:before, &:after': { display: 'none' },
};

// Standardised to MUI's LinkedIn glyph — the same icon startup-web uses in its
// profile view — coloured with the shared `indigo600` token so it matches
// startup-web exactly (that token resolves to black in both apps' themes).
export const LinkedInIcon = ({ size = 18 }) => {
  const theme = useTheme();
  return <LinkedInMuiIcon sx={{ fontSize: size, color: theme.palette.customColors.indigo600 }} />;
};

export const FieldLabel = ({ children, required, optional, sx = {} }) => (
  <Typography
    component="label"
    sx={{
      display: 'block',
      mb: '8px',
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#344054',
      ...sx,
    }}
  >
    {children}
    {required && <span style={{ color: '#F04438', marginLeft: 3 }}>*</span>}
    {optional && (
      <span style={{ color: PLACEHOLDER, fontWeight: 400, marginLeft: 6 }}>(Optional)</span>
    )}
  </Typography>
);

// Merge one level deep. A shallow spread would let extraSx's '& .MuiFilledInput-root'
// replace fieldSx's whole root block (border, background, radius) — and then the
// theme's global MuiTextField override (`border: 1px solid #A1A3A7`) shows through.
const mergeSx = (base, extra) => {
  const out = { ...base };
  Object.entries(extra).forEach(([key, value]) => {
    const existing = out[key];
    out[key] =
      value && typeof value === 'object' && !Array.isArray(value) &&
      existing && typeof existing === 'object' && !Array.isArray(existing)
        ? { ...existing, ...value }
        : value;
  });
  return out;
};

export const TextInput = ({
  icon: Icon,
  iconGradient = false,
  error,
  prefix,
  value,
  onChange,
  extraSx = {},
  ...props
}) => (
  <TextField
    fullWidth
    variant="filled"
    autoComplete="off"
    error={!!error}
    helperText={error ? <span style={{ color: '#F04438', fontSize: '0.75rem' }}>{error}</span> : ''}
    value={value ?? ''}
    onChange={(e) => {
      if (onChange) {
        const trimmedValue = e.target.value.trimStart();
        onChange({ ...e, target: { ...e.target, value: trimmedValue } });
      }
    }}
    sx={mergeSx(fieldSx, extraSx)}
    InputProps={{
      disableUnderline: true,
      startAdornment:
        Icon || prefix ? (
          <InputAdornment position="start">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                ...(prefix && { pr: 1.2, borderRight: `1px solid ${BORDER}` }),
              }}
            >
              {Icon && <FieldIcon icon={Icon} size={18} gradient={iconGradient} />}
              {prefix && (
                <Box
                  component="span"
                  sx={{ color: TEXT_COLOR, fontWeight: 500, fontSize: '0.95rem' }}
                >
                  {prefix}
                </Box>
              )}
            </Box>
          </InputAdornment>
        ) : null,
    }}
    {...props}
  />
);

export const SectionTitle = ({ children, sx = {} }) => (
  <Typography
    sx={{
      fontSize: { xs: '1.05rem', sm: '1.25rem' },
      fontWeight: 700,
      color: TEXT_COLOR,
      mb: 2.5,
      ...sx,
    }}
  >
    {children}
  </Typography>
);

// Verbatim from startup-web's onboarding-v2 (index.js:280): 30px circle, label
// beside the number, Check when done, filled INDIGO when active/done.
export const StepBadge = ({ index, active, done, label }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
    <Box
      sx={{
        width: 30,
        height: 30,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '0.85rem',
        fontWeight: 600,
        flexShrink: 0,
        color: active || done ? '#fff' : '#98A2B3',
        background: active || done ? INDIGO : 'transparent',
        border: active || done ? 'none' : `1.5px solid #D0D5DD`,
      }}
    >
      {done ? <Check size={16} /> : index}
    </Box>
    <Typography
      sx={{
        fontSize: { xs: '0.8rem', sm: '0.95rem' },
        fontWeight: active ? 600 : 500,
        color: active || done ? TEXT_COLOR : '#98A2B3',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Typography>
  </Box>
);

export const Stepper = ({ steps, currentStep }) => (
  <Box
    sx={{
      background: PAGE_BG,
      py: 2.5,
      px: 2,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: { xs: 1.5, sm: 3 },
      position: 'sticky',
      top: '65px',
      zIndex: 9,
    }}
  >
    {steps.map((label, i) => {
      const stepNo = i + 1;
      return (
        <React.Fragment key={label}>
          {i > 0 && (
            <Box
              sx={{
                width: { xs: 32, sm: 120 },
                height: '2px',
                background: currentStep > stepNo - 1 ? INDIGO : '#E4E7EC',
              }}
            />
          )}
          <StepBadge
            index={stepNo}
            active={currentStep === stepNo}
            done={currentStep > stepNo}
            label={label}
          />
        </React.Fragment>
      );
    })}
  </Box>
);

// showArrow defaults to true, so any existing caller keeps its arrow. In the Figma
// only Step 1's "Next" has one — every "Back" and "Get Started" is plain.
export const NextButton = ({ loading, label = 'Next', onClick, showArrow = true, ...props }) => (
  <Primary
    onClick={loading ? undefined : onClick}
    disabled={loading}
    sx={{ px: { xs: 2, sm: 4 }, py: 1.6, minWidth: { xs: '120px', sm: '160px' } }}
    {...props}
  >
    {loading ? (
      <CircularProgress size={20} sx={{ color: '#fff' }} />
    ) : (
      <Box
        component="span"
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}
      >
        {label} {showArrow && <ArrowRight size={18} />}
      </Box>
    )}
  </Primary>
);
